import { EnvironmentOutlined, GlobalOutlined, HeartOutlined, MailOutlined, NotificationOutlined, PlusCircleOutlined, QrcodeOutlined, SearchOutlined, ShopOutlined, StarOutlined } from '@ant-design/icons';
import { Card, Input, message, Switch, Tag } from 'antd';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import { DrawerContext } from '../../../store/DrawerContext/DrawerContext';
import { useFetch } from '../../../hooks/useFetch/useFetch';
import { authFetch } from '../../../common/request/authFetch';
import { ButtonLZ } from '../../components/design/Buttons/ButtonLZ';
import TableLZM from '../../components/design/table/TableLZM';
import '../pages.scss'
import { GuestAddModal } from './guests/GuestAddModal';
import { authRequest } from '../../../common/request/authRequest';
import { useAuth } from '../../../store/AuthContext/AuthContext';
import { PageTitle } from '../common/PageTitle';
import { ModuleAccessGate } from '../common/roles/ModuleAccessGate';

const GUESTS_PAGE_INFO = 'Esta pantalla permite crear usuarios invitados, esto es usuarios que no necesitan tener una cuenta extra con permisos a los locales de GBP para poder acceder a la plataforma. Acceden indirectamente a través de la cuenta de google del usuario principal o padre que los invitó, eso si con restricciones de permisos (configurables desde esta misma pantalla) y restricciones de grupos (configurables en la pantalla de grupos).';

export const Guests = () => (
  <ModuleAccessGate module='USERS' title='Usuarios' pageInfo={GUESTS_PAGE_INFO}>
    <GuestsContent />
  </ModuleAccessGate>
);

const GuestsContent = () => {

  const [guestAddModalVisible, setGuestAddModalVisible] = useState(false);
  const [updateData, setUpdateData] = useState(false);    
  const { t } = useTranslation();
  
  const { profile} = useAuth();
  
    const STATUSES: any = 
		{
			CREATED: "Creado",
			ACTIVE: 'Activo',
      TO_EMAIL: 'Por Invitar'
		}

    const changeStatus = (field: string, email: string, status : boolean) => {
        
      const data = {
        guestEmail: email,
        field: field,
        active: status
      };
  
      authRequest(`${process.env.REACT_APP_API}/config/permissions/${field}`, { method: 'PATCH', data })
      .then((response) => {                        
          message.success('Estado cambiado con éxito');
          setUpdateData(!updateData);
      }).catch(() => {
          message.error('Error inesperado');
      });
    }
  
  const { data: usersData, isLoading } = useFetch(`${process.env.REACT_APP_API}/config`, {}, [updateData]);

  const sendEmail = (email: string) => {
    authFetch(`${process.env.REACT_APP_API}/users/`+ email + '/invitation', { method: 'POST' });
  }
  
  const setEnabled = (email: string, enabled: boolean) => {

    const uri = enabled 
    ? 
    `${process.env.REACT_APP_API}/users/`+ email + '/actions/unlock' 
    : 
    `${process.env.REACT_APP_API}/users/`+ email + '/actions/lock'

    authFetch(uri, { method: 'PATCH' })
    .then((isOk) => {
      if(isOk) {
        message.info(t('view.forms.modal.user.success'))
      } else {					
        message.error(t('view.forms.modal.user.error'))
      }
    }
  );
  }

  const columns = [{
		title: 'Email',
		key: 'email',
		dataIndex: 'email',
    render: (value:any, record:any) => {
      return <div>{record.email}</div>
    },
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
    }: FilterDropdownProps) => {
      return (
        <Input
          autoFocus
          placeholder="Email"
          value={selectedKeys[0]}
          onChange={(e: any) => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);
            confirm({ closeDropdown: false });
          }}
          onPressEnter={() => {
            confirm();
          }}
          onBlur={() => {
            confirm();
          }}
        ></Input>
      );
    },
    filterIcon: () => {
      return <SearchOutlined />;
    },
    onFilter: (value: any, record: any) => {
      return (record?.email.toLowerCase().includes(value.toLowerCase()));
    }
	},
  {
		title: t('dictionary.name'),
		key: 'name',
		dataIndex: 'name'
	},{
		title: t('dictionary.rolePosition'),
		key: 'summary',
		dataIndex: 'summary'
	},{
		title: 'Permisos',
		key: 'roles',
    render: (record: any) => {
      return (
        <div> 
          <Switch size='small' style={{marginRight:'5px'}} checkedChildren={<ShopOutlined/>} unCheckedChildren={<ShopOutlined/>} checked={record.editInfo} onChange={(e) => {changeStatus('info', record.email, e)}}/>                   
          <Switch size='small' style={{marginRight:'5px'}} checkedChildren={<StarOutlined/>} unCheckedChildren={<StarOutlined/>} checked={record.editReviews} onChange={(e) => {changeStatus('reviews', record.email, e)}}/>                   
          <Switch size='small' style={{marginRight:'5px'}} checkedChildren={<NotificationOutlined/>} unCheckedChildren={<NotificationOutlined/>} checked={record.editContent} onChange={(e) => {changeStatus('content', record.email, e)}}/>                                                       
          <Switch size='small' style={{marginRight:'5px'}} checkedChildren={<EnvironmentOutlined/>} unCheckedChildren={<EnvironmentOutlined/>} checked={record.editCompetitors} onChange={(e) => {changeStatus('competitors', record.email, e)}}/>
          <Switch size='small' style={{marginRight:'5px'}} checkedChildren={<HeartOutlined/>} unCheckedChildren={<HeartOutlined/>} checked={record.editSentiment} onChange={(e) => {changeStatus('sentiment', record.email, e)}}/>
          <Switch size='small' style={{marginRight:'5px'}} checkedChildren={<GlobalOutlined/>} unCheckedChildren={<GlobalOutlined/>} checked={record.editWeb} onChange={(e) => {changeStatus('web', record.email, e)}}/>
          <Switch size='small' style={{marginRight:'5px'}} checkedChildren={<QrcodeOutlined/>} unCheckedChildren={<QrcodeOutlined/>} checked={record.editQR} onChange={(e) => {changeStatus('qr', record.email, e)}}/>
        </div>    
      )
    }
	},{
		title: t('dictionary.state'),
		key: 'status',		
    render: (record: any) => {
      return (
      <>                
        { STATUSES[record.status]
        }
      </>
      )
    }
	},
  {
		title: t('dictionary.actions'),
		key: 'enabled',
    align: 'center',
    render: (record: any) => {      
      
      return record.status === 'CREATED' || record.status === 'TO_EMAIL' 
      ?
        <ButtonLZ color='black' size='small' disabled={record.status === 'CREATED'} onClick={() => {sendEmail(record.email); setUpdateData(!updateData)}}>
          <MailOutlined/>          
        </ButtonLZ>               
      :
      <Switch defaultChecked={record.enabled} size='small' onChange={() => {setEnabled(record.email, !record.enabled); setUpdateData(!updateData)}}/>          
    },
    filters: [
      { text: 'Activos', value: true },
      { text: 'Bloqueados', value: false },
    ],
    onFilter: (value: any, record: any) => record.enabled === value,
	}]

  return (
    <>
      <Card className='main-card-content'>

        <PageTitle
          title='Usuarios'
          pageInfo={GUESTS_PAGE_INFO}
        />
        <h3 style={{color:'steelblue'}}>Invitados de {profile.name}</h3>
                
        <div style={{display:'flex', justifyContent:'end', marginTop:'15px'}} >
          <ButtonLZ shape='round' onClick={() => setGuestAddModalVisible(true)} type='primary' >
            <PlusCircleOutlined/> Usuario 
          </ButtonLZ>
        </div>

        <TableLZM  columns={columns} dataSource={usersData} rowKey={'email'} loading={isLoading} />
        {guestAddModalVisible && <GuestAddModal          
          onFinish={() => {
            setGuestAddModalVisible(false);
            setUpdateData(!updateData);
          }}
        />}
      </Card>
    </>
	)
}
